import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { clsx } from 'clsx';
import DOMPurify from 'dompurify';
import { createElement, useEffect, useRef, useState } from 'react';
import { useIntervalWhen } from 'rooks';
import { DEFAULT_TOOL_TIP_DELAY } from './Tooltip.constants';
import type { Placement } from '@floating-ui/dom';

const getArrowStyle = (
  position: string,
  x: number | undefined,
  arrowWidth: number,
  tooltipHeight: number,
  tooltipWidth: number,
) => {
  switch (position) {
    case 'bottom':
      return {
        left: `${x}px`,
        top: `${-arrowWidth}px`,
      };

    case 'left':
      return {
        left: `${tooltipWidth - arrowWidth}px`,
        top: `${tooltipHeight / 2 - arrowWidth}px`,
      };
    case 'right':
      return {
        left: `${-arrowWidth}px`,
        top: `${tooltipHeight / 2 - arrowWidth}px`,
      };
    default: // 'top':
      return {
        left: `${x}px`,
        top: `${tooltipHeight - 10 + arrowWidth}px`,
      };
  }
};

type HTMLTipProps = {
  text: string;
};

const HTMLTip = ({ text }: HTMLTipProps) => {
  return createElement('div', {
    // oxlint-disable-next-line react/no-danger
    dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(text) },
  });
};

// Tooltips can cause system tests to be flaky, so we suppress them in Playwright and Protractor.
const suppressTooltip = () => {
  return window.navigator.userAgent === 'Playwright' || window.navigator.userAgent.includes('Protractor');
};

/**
 * QTip
 *
 * QTip is a Singleton Tooltip component that guarantees high-performance and reduces component wrappers!
 *
 * QTip is used by all qomponents that support the display of tooltips.
 * If you want to add a Tooltip to your application you can do so by adding the following html data-attributes:
 *
 *    'data-qtip-text': the tooltip text to display; this can also be a string containing valid HTML
 *    'data-qtip-placement': one of TooltipPosition (top, bottom, right, or left)
 *    'data-qtip-is-html': set this to true if you provided a text that contains HTML,
 *    'data-qtip-delay': this can be used to delay the showing of the tooltip. this should be a number representing
 *    the # of milliseconds you want to delay the tooltip for. If no delay is provided the DEFAULT_TOOL_TIP_DELAY is
 *    applied!
 *    'data-qtip-testid': use this attribute to provide a value for a data-testid of your tooltip; this is useful
 *    for tests
 *
 * In order for QTip to be able to display Tooltips you must add the QTip component to your top-most component
 * (often that's App or Application) - simply add:
 *
 *    <QTip />
 *
 * and enjoy beautiful & performant tooltips!
 */
export const QTip = () => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const tooltipTarget = useRef<HTMLDivElement | null>(null);
  const tooltipArrowRef = useRef<HTMLDivElement>(null);

  const [tooltipText, setTooltipText] = useState('');
  const [tooltipTargetOriginalPosition, setTooltipTargetOriginalPosition] = useState<DOMRect | null>(null);
  const [show, setShow] = useState(false);
  const [html, setHtml] = useState(false);
  const [tooltipTestId, setTooltipTestId] = useState<string | undefined>('');
  const [overTooltip, setOverTooltip] = useState(false);

  // Check every 300ms if the tooltip target position has changed and hide the tooltip if this happens.
  useIntervalWhen(
    () => {
      const currentPosition = tooltipTarget?.current?.getBoundingClientRect();

      if (overTooltip) {
        return;
      }

      if (
        tooltipTargetOriginalPosition?.y !== currentPosition?.y ||
        tooltipTargetOriginalPosition?.x !== currentPosition?.x
      ) {
        setShow(false);
      }
    },
    300,
    show,
  );

  const ttTimeout = useRef<NodeJS.Timeout>(null);

  const findTooltipData = (element: HTMLElement | null): DOMStringMap | null => {
    // Traverse up to 8 levels to find the tooltip data
    let currentElement: HTMLElement | null = element;
    for (let i = 0; i < 8 && currentElement; i++) {
      if (!currentElement) {
        return null;
      }
      if (currentElement?.dataset?.qtipText) {
        return currentElement.dataset;
      }
      currentElement = currentElement?.parentElement;
    }
    return null;
  };

  const makeTooltip = (
    tooltipText: string | undefined,
    isHtml: boolean,
    dataTestId: string | undefined,
    delay: number,
    position = 'top',
  ) => {
    if (tooltipText && tooltipTarget.current) {
      setHtml(isHtml);
      setTooltipText(tooltipText);
      setTooltipTestId(dataTestId);
      setTooltipTargetOriginalPosition(tooltipTarget.current.getBoundingClientRect());

      const positionTooltip = () => {
        if (tooltipRef.current && tooltipTarget.current && tooltipArrowRef.current) {
          // oxlint-disable-next-line typescript/no-floating-promises
          computePosition(tooltipTarget.current, tooltipRef.current, {
            placement: position as Placement,
            middleware: [offset(10), flip(), shift({ padding: 8 }), arrow({ element: tooltipArrowRef.current })],
          }).then(({ x, y, middlewareData, placement: finalPlacement }) => {
            if (!tooltipRef.current) {
              return;
            }

            Object.assign(tooltipRef.current.style, {
              left: `${x}px`,
              top: `${y}px`,
            });
            if (middlewareData.arrow && tooltipTarget.current && tooltipArrowRef.current) {
              const { x, y } = middlewareData.arrow;
              const arrowWidth = tooltipArrowRef.current.offsetHeight / 2;
              const tooltipHeight = tooltipRef.current.offsetHeight;
              const tooltipWidth = tooltipRef.current.offsetWidth;
              const style = getArrowStyle(finalPlacement, x || y, arrowWidth, tooltipHeight, tooltipWidth);
              Object.assign(tooltipArrowRef.current.style, style);
            }
            setShow(true);
          });
        }
      };

      delay > 0
        ? requestAnimationFrame(() => {
            positionTooltip();
          })
        : positionTooltip();
    }
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent): void => {
      if (ttTimeout.current) {
        clearTimeout(ttTimeout.current);
      }

      // Find tooltip data on the current or parent elements
      const target = e.target as HTMLDivElement | null;
      const dataset = findTooltipData(target);

      if (!dataset) {
        // No tooltip data found
        tooltipTarget.current = null;
        return;
      }

      tooltipTarget.current = target;
      const text = dataset.qtipText;

      if (text) {
        const delay = parseInt(dataset.qtipDelay || `${DEFAULT_TOOL_TIP_DELAY}`, 10);
        const placement = dataset.qtipPlacement || 'top';
        const isHtml = dataset.qtipIsHtml === 'true';
        const testId = dataset.qtipTestid;

        // Debounced tooltip display
        ttTimeout.current = setTimeout(() => {
          makeTooltip(text, isHtml, testId, delay, placement);
        }, delay);
      }
    };

    !suppressTooltip() && document.body.addEventListener('mousemove', onMouseMove);
    return () => {
      !suppressTooltip() && document.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  return (
    <>
      <div
        onMouseEnter={() => setOverTooltip(true)}
        onMouseLeave={() => setOverTooltip(false)}
        data-testid={tooltipTestId}
        ref={tooltipRef}
        className={clsx(
          'tw:absolute tw:z-9999 tw:max-w-96 tw:rounded-sm tw:bg-black tw:p-2 tw:text-xs tw:text-sq-white',
          {
            'tw:visible tw:animate-fadeIn': show,
            'tw:pointer-events-none tw:invisible': !show,
          },
        )}>
        {html ? <HTMLTip text={tooltipText} /> : tooltipText}
        <div className="tw:absolute tw:h-2.5 tw:w-2.5 tw:rotate-45 tw:bg-black" ref={tooltipArrowRef}></div>
      </div>
    </>
  );
};
